import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { fromEvent, ReplaySubject } from 'rxjs';
import * as GetActivityStatusOrg from '../../server/routes/activity-status/get';
import * as ActivityStatusPost from '../../server/routes/activity-status/post';
import * as GetActivityStatusUser from '../../server/routes/activity-status/user/get';
import {
  type ActivityStatusStringType,
  activityStatusTimeout,
  activityStatusTimeoutBuffer,
} from '../../server/services/activity-status-constants.service';
import { generatePath } from '../../server/services/utils.service';
import { AuthUserService } from './auth-user.service';
import { WebSocketService } from './web-socket.service';

// How the activity status service works
// All windows across all user's devices are kept track of separately
// A post is made every 10 minutes on an active window
// When posting an updated status, we don't send it to other users unless it
//   changes the state. We do not send an update to the expiresAt.
// If someone observing a user sees an activity has expired, it asks the
//   backend for the current state before setting as inactive.
// This way the service can get the latest activity but not be constantly
//   bombarded with messages from every open window from a user.
// It is important that the service only requests the new activity status AFTER
//   the expiration of the old one, otherwise the GET might return the old
//   state.

interface SubscriptionElement {
  orgId: string;
  userId: string;
  expiresAt: number | undefined;
  activityStatusString: ActivityStatusStringType;
  activityStatusReplaySubject: ReplaySubject<ActivityStatusStringType>;
  timeoutRef?;
}

@Injectable({
  providedIn: 'root',
})
export class ActivityStatusService {
  private http = inject(HttpClient);
  private authUserService = inject(AuthUserService);
  private webSocketService = inject(WebSocketService);

  private accountId: string | undefined;
  private orgId: string | undefined;
  private userId: string | undefined;
  private activitySubscriptions: SubscriptionElement[] = [];
  private activityInterval;

  private lastActiveAt = 0;

  constructor() {
    // Track events
    fromEvent(document, 'mousemove').subscribe(() => this.trackEvent());
    fromEvent(document, 'touchstart').subscribe(() => this.trackEvent());
    fromEvent(document, 'keydown').subscribe(() => this.trackEvent());

    this.webSocketService.pushObservable.subscribe((socketResult) => {
      if (
        socketResult.service === 'activityStatus' &&
        socketResult.action === 'update'
      ) {
        const { orgId } = socketResult;
        const { userId, status, expiresAt } = socketResult.data;
        const oldEntry = this.activitySubscriptions.find(
          (s) => s.userId === userId
        );

        // We place this here to get the undefined value
        const oldExpiresAt = oldEntry?.expiresAt;
        const oldStatusString = oldEntry?.activityStatusString;

        let entry: SubscriptionElement;
        if (oldEntry) {
          entry = oldEntry;
        } else {
          entry = {
            orgId,
            userId,
            expiresAt,
            activityStatusString: status,
            activityStatusReplaySubject:
              new ReplaySubject<ActivityStatusStringType>(),
          };
          this.activitySubscriptions.push(entry);
        }

        // Time out the existing
        if (oldExpiresAt !== expiresAt) {
          if (entry?.timeoutRef) clearTimeout(entry.timeoutRef);
          this.mutateEntrySetExpiresAt(entry, expiresAt);
        }

        if (oldStatusString !== status) {
          entry.activityStatusReplaySubject.next(status);
        }
      }
    });

    this.authUserService.userOrgObj.subscribe((userOrgObj) => {
      if (userOrgObj) {
        const userId = userOrgObj.user._id;
        if (this.userId !== userId) {
          // TODO: clear old activity status from the prior user

          const { accountId } = userOrgObj;
          const orgId = userOrgObj.org._id;
          this.accountId = accountId;
          this.orgId = orgId;
          this.userId = userId;
          this.startActivityStatus({ orgId });
        }
      } else {
        this.userId = undefined;
        this.orgId = undefined;
        this.clearActivityMonitoring();
      }
    });
  }

  trackEvent() {
    this.lastActiveAt = Date.now();
  }

  // Gets the first load activity status for the listed logged in user
  private startActivityStatus({ orgId }: { orgId: string }) {
    this.clearActivityMonitoring();
    this.http
      .get<GetActivityStatusOrg.ResBody>(
        generatePath<GetActivityStatusOrg.ReqParams>(
          '/api/orgs/:orgId/activity-status',
          { orgId }
        )
      )
      .subscribe((results) => {
        results.forEach(({ userId, status, expiresAt }) => {
          const entry: SubscriptionElement = {
            orgId,
            userId,
            expiresAt,
            activityStatusString: status,
            activityStatusReplaySubject:
              new ReplaySubject<ActivityStatusStringType>(),
          };
          this.mutateEntrySetExpiresAt(entry, expiresAt);
          entry.activityStatusReplaySubject.next(status);
        });
      });

    // Set up a new interval

    this.activityInterval = setInterval(() => {
      // TODO: // Do we need && navigator.onLine ??

      const appInstanceId = this.authUserService.appInstanceId;
      const expiresAt = Date.now() + activityStatusTimeout;
      const body: ActivityStatusPost.ReqBody = {
        status: 'active',
        appInstanceId,
        expiresAt,
      };
      this.http
        .post<ActivityStatusPost.ResBody>(
          generatePath<ActivityStatusPost.ReqParams>(
            '/api/orgs/:orgId/activity-status',
            { orgId }
          ),
          body
        )
        .subscribe((response) => {
          // Do nothing
        });
    }, activityStatusTimeout - activityStatusTimeoutBuffer); // Do refresh 5 seconds before it is due to timeout
  }

  private mutateEntrySetExpiresAt(
    entry: SubscriptionElement,
    expiresAt: number | undefined
  ) {
    if (expiresAt) {
      const timeToWait = 10 + expiresAt - Date.now();
      const timeoutRef = setTimeout(() => {
        // We use entry.expiresAt below because it may have been updated
        const currentExpiresAt = entry.expiresAt;
        if (currentExpiresAt && currentExpiresAt < Date.now()) {
          // Try one last time to get the user's activity
          const orgId = entry.orgId;
          const userId = entry.userId;

          // Double check that we are still in the same org
          // We can use whatever accountId in the org, it should not matter when
          // looking at others' user status
          if (orgId !== this.orgId || !this.accountId) {
            entry.activityStatusString = 'inactive';
            entry.activityStatusReplaySubject.next('inactive');
            return;
          }
          this.http
            .get<GetActivityStatusUser.ResBody>(
              generatePath<GetActivityStatusUser.ReqParams>(
                '/api/orgs/:orgId/users/:userId/activity-status',
                { orgId, userId }
              )
            )
            .subscribe({
              next: ({ status: newStatus, expiresAt: newExpiresAt }) => {
                entry.activityStatusString = newStatus;
                this.mutateEntrySetExpiresAt(entry, newExpiresAt);
                entry.activityStatusReplaySubject.next(newStatus);
              },
              error: (err) => {
                entry.activityStatusString = 'inactive';
                entry.activityStatusReplaySubject.next('inactive');
              },
            });
        }
      }, timeToWait);
      entry.timeoutRef = timeoutRef;
      entry.expiresAt = expiresAt;
    }
  }

  private clearActivityMonitoring(): void {
    this.activitySubscriptions.forEach((a) =>
      a.activityStatusReplaySubject.complete()
    );

    this.activitySubscriptions = [];

    this.stopActivityMonitoring();
  }

  private stopActivityMonitoring() {
    if (this.activityInterval) clearInterval(this.activityInterval);
  }

  userActivityStatus({
    orgId,
    userId,
  }: {
    orgId: string;
    userId: string;
  }): ReplaySubject<ActivityStatusStringType> {
    const oldEntry = this.activitySubscriptions.find(
      (s) => s.userId === userId
    );
    let entry: SubscriptionElement;
    if (oldEntry) {
      entry = oldEntry;
    } else {
      entry = {
        orgId,
        userId,
        expiresAt: undefined,
        activityStatusString: 'inactive',
        activityStatusReplaySubject:
          new ReplaySubject<ActivityStatusStringType>(),
      };
      entry.activityStatusReplaySubject.next('inactive');
      this.activitySubscriptions.push(entry);
    }

    return entry.activityStatusReplaySubject;
  }
}
